import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { PageHeader, LinkButton, Table, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { ExportCsvButton } from "@/components/admin/ExportCsvButton";
import { ImportCsvButton } from "@/components/admin/ImportCsvButton";

export default function AdminCertificationsList() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin-certifications"],
    queryFn: async () => (await api.get("/admin/materials-certifications")).data,
  });
  const { sort, toggleSort, sortRows } = useTableSort();
  const sorted = sortRows(data, (c, key) => {
    if (key === "is_public") return c.is_public ? 1 : 0;
    return c[key] ?? null;
  });

  async function remove(c) {
    if (!window.confirm(`Delete certification "${c.name}"? It will also be detached from any brands and clothing items currently using it.`)) return;
    try {
      await api.delete(`/admin/materials-certifications/${c.id}`);
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-certifications"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div data-testid="admin-certifications-list">
      <PageHeader
        title="Certifications"
        subtitle={`${data.length} certifications`}
        actions={
          <div className="flex gap-2">
            <ImportCsvButton
              endpoint="/admin/materials-certifications/import.csv"
              testid="import-certifications-csv"
              onSuccess={() => qc.invalidateQueries({ queryKey: ["admin-certifications"] })}
            />
            <ExportCsvButton
              endpoint="/admin/materials-certifications/export.csv"
              filename="certifications.csv"
              testid="export-certifications-csv"
            />
            <LinkButton to="/admin/materials-certifications/new">+ New certification</LinkButton>
          </div>
        }
      />
      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Slug" sortKey="slug" sort={sort} onSort={toggleSort} />
              <SortableHeader label="External link" sortKey="external_link" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Public" sortKey="is_public" sort={sort} onSort={toggleSort} />
              <SortableHeader label="" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]" data-testid={`cert-row-${c.id}`}>
                <td className="px-3 py-3"><Link to={`/admin/materials-certifications/${c.id}`} className="font-medium hover:underline">{c.name}</Link></td>
                <td className="px-3 py-3 text-xs text-[#6C757D]">{c.slug}</td>
                <td className="px-3 py-3 text-xs">{c.external_link ? <a href={c.external_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{c.external_link.slice(0, 50)}{c.external_link.length > 50 ? "…" : ""}</a> : "—"}</td>
                <td className="px-3 py-3 text-xs">{c.is_public ? "Yes" : "No"}</td>
                <td className="px-3 py-3 text-sm whitespace-nowrap">
                  <Link to={`/admin/materials-certifications/${c.id}`} className="underline mr-3" data-testid={`cert-edit-${c.id}`}>Edit</Link>
                  <button type="button" onClick={() => remove(c)} className="text-red-600 underline" data-testid={`cert-delete-${c.id}`}>Delete</button>
                </td>
              </tr>
            ))}
            {!isLoading && sorted.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[#6C757D]">No certifications yet.</td></tr>}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
